import React, { useEffect, useState } from 'react';
import { Loader2, Palette, Plus, Save, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { useBranding } from '@/lib/branding';

// Fix (found during a Phase-0 branding audit): the backend has had a fully
// working GET/PUT /api/admin/platform-settings since Phase 0, but no page,
// route, or nav link ever exposed it — a super_admin had no way to change
// the app name, logo, accent color, or support contact except editing the
// database directly. This page is that missing piece.
const DEFAULT_REFUND_TIERS = [
  { hours_before: 24, refund_percent: 100 },
  { hours_before: 2, refund_percent: 50 },
  { hours_before: 0, refund_percent: 0 },
];

const empty = {
  app_name: '',
  short_code: '',
  support_email: '',
  support_phone: '',
  logo_url: '',
  primary_color: '#CCFF00',
  refund_tiers: DEFAULT_REFUND_TIERS,
  support_page_content: '',
  refund_policy_content: '',
  nearby_radius_km: 10,
  // Platform-fee billing (venues pay Kalgyan Solutions monthly via AutoPay). The fee
  // amounts themselves are set per venue/plan on the Venues page.
  min_invoice_amount: 100,
  mandate_max_amount: 15000,
  invoice_due_days: 5,
  overdue_pause_days: 7,
  require_fee_mandate: false,
  billing_legal_name: '',
  billing_address: '',
  gstin: '',
  gst_rate: 18,
  sac_code: '998599',
};

export default function AdminSettings() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { refresh: refreshBranding } = useBranding();

  useEffect(() => {
    api.get('/admin/platform-settings')
      .then(({ data }) => setForm({ ...empty, ...data }))
      .catch((err) => toast({ title: 'Failed to load settings', description: err.response?.data?.detail || '', variant: 'error' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const setTier = (i, patch) => setForm({ ...form, refund_tiers: form.refund_tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) });
  const addTier = () => setForm({ ...form, refund_tiers: [...form.refund_tiers, { hours_before: 1, refund_percent: 0 }] });
  const removeTier = (i) => setForm({ ...form, refund_tiers: form.refund_tiers.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    if (!form.refund_tiers.some((t) => Number(t.hours_before) === 0)) {
      toast({ title: 'Missing 0-hour tier', description: "Refund tiers must include one with 0 hours' notice (the no-notice floor).", variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        app_name: form.app_name,
        short_code: form.short_code,
        support_email: form.support_email || null,
        support_phone: form.support_phone || null,
        logo_url: form.logo_url || null,
        primary_color: form.primary_color || null,
        refund_tiers: form.refund_tiers.map((t) => ({ hours_before: Number(t.hours_before), refund_percent: Number(t.refund_percent) })),
        support_page_content: form.support_page_content || undefined,
        refund_policy_content: form.refund_policy_content || undefined,
        nearby_radius_km: Number(form.nearby_radius_km) || 10,
        min_invoice_amount: Number(form.min_invoice_amount) || 0,
        mandate_max_amount: Number(form.mandate_max_amount) || 15000,
        invoice_due_days: Number(form.invoice_due_days) || 0,
        overdue_pause_days: Number(form.overdue_pause_days) || 0,
        require_fee_mandate: !!form.require_fee_mandate,
        billing_legal_name: form.billing_legal_name || null,
        billing_address: form.billing_address || null,
        gstin: form.gstin || null,
        gst_rate: Number(form.gst_rate) || 0,
        sac_code: form.sac_code || null,
      };
      const { data } = await api.put('/admin/platform-settings', payload);
      setForm({ ...empty, ...data });
      toast({ title: 'Settings saved', description: 'App name, logo, and accent color update everywhere immediately.', variant: 'success' });
      refreshBranding(); // apply the new branding across the app right away, no reload needed
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || '', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white/40 text-sm py-12 text-center">Loading settings...</div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Platform</div>
        <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Settings</h1>
        <p className="mt-2 text-sm text-white/50">
          Controls the app name, logo, accent color, and support contact shown across the customer
          and admin apps — takes effect immediately, no rebuild needed.
        </p>
      </div>

      <form onSubmit={save} className="bg-surface border border-white/10 p-6 sm:p-8 max-w-2xl space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="App Name" hint="Shown in the header, browser tab, and emails.">
            <Input required maxLength={40} value={form.app_name} onChange={set('app_name')} placeholder="PaceSlot" data-testid="settings-app-name" />
          </Field>
          <Field label="Short Code" hint="Prefix for member codes, e.g. KBA0001.">
            <Input
              required
              maxLength={10}
              value={form.short_code}
              onChange={(e) => setForm({ ...form, short_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
              placeholder="KBA"
              data-testid="settings-short-code"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Support Email">
            <Input type="email" value={form.support_email || ''} onChange={set('support_email')} placeholder="support@example.com" data-testid="settings-support-email" />
          </Field>
          <Field label="Support Phone">
            <Input value={form.support_phone || ''} onChange={set('support_phone')} placeholder="+91 9876543210" data-testid="settings-support-phone" />
          </Field>
        </div>

        <Field label="Logo URL" hint="Square image, shown in the header logo mark.">
          <Input value={form.logo_url || ''} onChange={set('logo_url')} placeholder="https://…" data-testid="settings-logo-url" />
        </Field>

        <Field label="Accent Color" hint="The app's highlight color — buttons, active states, badges.">
          <div className="flex items-center gap-3">
            <div className="relative w-11 h-11 shrink-0 border border-white/20 overflow-hidden">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(form.primary_color || '') ? form.primary_color : '#CCFF00'}
                onChange={set('primary_color')}
                className="absolute -top-1 -left-1 w-[calc(100%+8px)] h-[calc(100%+8px)] cursor-pointer border-0 p-0"
                data-testid="settings-color-picker"
                aria-label="Accent color picker"
              />
            </div>
            <Input
              value={form.primary_color || ''}
              onChange={set('primary_color')}
              placeholder="#CCFF00"
              className="font-mono uppercase"
              data-testid="settings-color-hex"
            />
            <div className="flex items-center gap-1.5 text-xs text-white/40 whitespace-nowrap pl-1">
              <Palette size={14} /> Preview above
            </div>
          </div>
        </Field>

        <Field label="Cancellation refund policy" hint="How much a customer gets back when they cancel their own booking, based on notice given. Venue-initiated cancellations always refund in full regardless of this policy.">
          <div className="space-y-3">
            {form.refund_tiers.map((t, i) => (
              <div key={i} className="flex items-end gap-3">
                <Field label="Hours' notice">
                  <Input type="number" min="0" value={t.hours_before} onChange={(e) => setTier(i, { hours_before: e.target.value })} data-testid={`refund-tier-hours-${i}`} />
                </Field>
                <Field label="Refund %">
                  <Input type="number" min="0" max="100" value={t.refund_percent} onChange={(e) => setTier(i, { refund_percent: e.target.value })} data-testid={`refund-tier-percent-${i}`} />
                </Field>
                <Button type="button" variant="outline" size="sm" onClick={() => removeTier(i)} data-testid={`refund-tier-remove-${i}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addTier} data-testid="refund-tier-add">
              <Plus size={14} /> Add tier
            </Button>
          </div>
        </Field>

        <Field label="Support page content" hint="Shown on the public /support page. Contact details above are shown automatically alongside it.">
          <Textarea
            value={form.support_page_content || ''}
            onChange={set('support_page_content')}
            className="min-h-[140px]"
            placeholder="Need help? Reach out and we'll get back to you as soon as we can."
            data-testid="settings-support-content"
          />
        </Field>

        <Field label="Refund & cancellation policy content" hint="Shown on the public /refund-policy page, above the refund tiers table.">
          <Textarea
            value={form.refund_policy_content || ''}
            onChange={set('refund_policy_content')}
            className="min-h-[140px]"
            placeholder="Cancellations made with enough notice are eligible for a refund…"
            data-testid="settings-refund-policy-content"
          />
        </Field>

        <Field
          label="Venue discovery radius (km)"
          hint="Customers see venues within this distance of their location (GPS or a searched address) when picking where to play."
        >
          <Input type="number" min="1" max="500" step="1" value={form.nearby_radius_km} onChange={set('nearby_radius_km')} data-testid="settings-nearby-radius" />
        </Field>

        <div className="border-t border-white/10 pt-6 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-1">Platform-fee billing</div>
            <p className="text-xs text-white/50">
              Venues receive customer payments directly. Your per-booking and per-plan fees (set on the Venues page) are
              invoiced on the 1st of each month and collected through each venue's AutoPay mandate.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Minimum invoice (₹)" hint="Smaller monthly totals carry forward to next month.">
              <Input type="number" min="0" value={form.min_invoice_amount} onChange={set('min_invoice_amount')} data-testid="settings-min-invoice" />
            </Field>
            <Field label="AutoPay limit (₹)" hint="Max per debit the venue approves. Bigger bills go out as a payment link.">
              <Input type="number" min="1" value={form.mandate_max_amount} onChange={set('mandate_max_amount')} data-testid="settings-mandate-max" />
            </Field>
            <Field label="Invoice due after (days)">
              <Input type="number" min="0" value={form.invoice_due_days} onChange={set('invoice_due_days')} data-testid="settings-due-days" />
            </Field>
            <Field label="Pause bookings when overdue by (days)" hint="Already-paid bookings are always honoured.">
              <Input type="number" min="0" value={form.overdue_pause_days} onChange={set('overdue_pause_days')} data-testid="settings-pause-days" />
            </Field>
          </div>
          <label className="flex items-start gap-2 text-sm text-white/70 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.require_fee_mandate}
              onChange={(e) => setForm({ ...form, require_fee_mandate: e.target.checked })}
              className="accent-volt mt-1"
              data-testid="settings-require-mandate"
            />
            <span>
              Require an active AutoPay mandate before a venue can take online payments
              <span className="block text-xs text-white/40">Applies once Razorpay partner keys are configured. Leave off until AutoPay is enabled on your Razorpay account.</span>
            </span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Legal name on invoices" hint="Defaults to the app name.">
              <Input value={form.billing_legal_name || ''} onChange={set('billing_legal_name')} data-testid="settings-billing-name" />
            </Field>
            <Field label="GSTIN" hint="Leave blank until GST-registered — no GST is added without it.">
              <Input
                value={form.gstin || ''}
                maxLength={15}
                onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                placeholder="Not registered"
                className="font-mono uppercase"
                data-testid="settings-gstin"
              />
            </Field>
            <Field label="GST rate (%)">
              <Input type="number" min="0" max="28" value={form.gst_rate} onChange={set('gst_rate')} data-testid="settings-gst-rate" />
            </Field>
            <Field label="SAC code">
              <Input value={form.sac_code || ''} onChange={set('sac_code')} data-testid="settings-sac" />
            </Field>
          </div>
          <Field label="Address on invoices">
            <Textarea value={form.billing_address || ''} onChange={set('billing_address')} className="min-h-[80px]" data-testid="settings-billing-address" />
          </Field>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={saving} data-testid="save-settings">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save Settings</>}
          </Button>
        </div>
      </form>
    </div>
  );
}
